import { useEffect, useId, useState } from 'react';
import { ISLAND_CODE_LENGTH, isValidIslandCode } from '@neopolis/engine';
import type { Content } from '@neopolis/engine';

import { T } from '../textes-interface.js';

/**
 * L'écran d'accueil : un code d'îlot, rien d'autre.
 *
 * Règle non négociable n° 5 : c'est la seule identification demandée à un élève.
 * Pas de nom, pas d'email.
 */
export function AccueilEcran({
  content,
  ilotReprenable,
  erreur,
  onCommencer,
  onReprendre,
  onMentions,
  onEffacerErreur,
}: {
  content: Content;
  ilotReprenable: string | null;
  erreur: string | null;
  onCommencer: (code: string) => void;
  onReprendre: () => void;
  onMentions: () => void;
  /**
   * Appelé dès que l'élève retouche son code : le refus précédent ne doit pas
   * rester à l'écran à côté du nouveau message, sans quoi un groupe lit deux
   * reproches contradictoires en cherchant son erreur.
   */
  onEffacerErreur: () => void;
}) {
  const [code, setCode] = useState('');
  const [touche, setTouche] = useState(false);
  const idChamp = useId();
  const idAide = useId();
  const idErreur = useId();

  const codeNormalise = code.trim().toUpperCase();
  const valide = isValidIslandCode(codeNormalise);
  const montrerErreur = touche && codeNormalise.length > 0 && !valide;

  useEffect(() => {
    document.title = content.meta.titre;
  }, [content.meta.titre]);

  return (
    <main className="mx-auto flex min-h-screen max-w-xl flex-col justify-center gap-8 p-6">
      <header className="text-center">
        <p aria-hidden="true" className="text-5xl">
          🏙️
        </p>
        <h1 className="mt-3 text-4xl font-bold text-slate-900">{content.meta.titre}</h1>
        <p className="mt-2 text-lg text-slate-600">{T.accueil.sousTitre}</p>
        <p className="mt-1 text-sm text-slate-500">
          {content.meta.discipline} — {content.meta.niveau}
        </p>
      </header>

      <form
        className="flex flex-col gap-3"
        onSubmit={(evenement) => {
          evenement.preventDefault();
          setTouche(true);
          if (valide) {
            onCommencer(codeNormalise);
          }
        }}
      >
        <label htmlFor={idChamp} className="font-semibold text-slate-800">
          {T.accueil.labelCode}
        </label>

        <input
          id={idChamp}
          value={code}
          onChange={(evenement) => {
            setCode(evenement.target.value);
            if (erreur !== null) {
              onEffacerErreur();
            }
          }}
          onBlur={() => setTouche(true)}
          maxLength={ISLAND_CODE_LENGTH}
          autoComplete="off"
          autoCapitalize="characters"
          spellCheck={false}
          inputMode="text"
          aria-describedby={montrerErreur ? `${idAide} ${idErreur}` : idAide}
          aria-invalid={montrerErreur}
          className="min-h-touch rounded-xl border-2 border-slate-300 px-4 text-center text-3xl font-bold uppercase tracking-[0.3em] focus:border-sky-600"
        />

        <p id={idAide} className="text-sm text-slate-500">
          {T.accueil.aideCode}
        </p>

        {montrerErreur && (
          <p id={idErreur} role="alert" className="text-sm font-medium text-rose-700">
            {T.accueil.codeInvalide}
          </p>
        )}

        <button
          type="submit"
          disabled={!valide}
          className="min-h-touch rounded-xl bg-sky-600 px-6 text-lg font-semibold text-white disabled:bg-slate-300 disabled:text-slate-500"
        >
          {T.accueil.commencer}
        </button>
      </form>

      {ilotReprenable !== null && (
        <section className="rounded-2xl border border-slate-200 bg-white p-4 text-center">
          <p className="text-sm text-slate-600">{T.accueil.reprendreAide}</p>
          <button
            type="button"
            onClick={onReprendre}
            className="mt-3 min-h-touch w-full rounded-xl bg-white px-6 font-semibold text-sky-700 ring-2 ring-sky-600 hover:bg-sky-50"
          >
            {T.accueil.reprendre}
            <span className="ml-2 font-mono tracking-widest">{ilotReprenable}</span>
          </button>
        </section>
      )}

      {erreur !== null && (
        <p role="alert" className="text-center text-sm font-medium text-rose-700">
          {erreur}
        </p>
      )}

      <footer className="text-center">
        <button
          type="button"
          onClick={onMentions}
          className="min-h-touch rounded-lg px-3 text-sm font-medium text-slate-600 underline hover:text-slate-900"
        >
          {T.mentions.lien}
        </button>
      </footer>
    </main>
  );
}
